<template>
  <v-row no-gutters>
    <v-col v-if="isFetched">
      <v-row no-gutters v-if="computedHistory.length === 0">
        <v-col class="mb-10 no-content">
          {{ $i18n.t('admin.noData') }}
        </v-col>
      </v-row>

      <sidebar-row
        v-for="(item, index) in computedHistory"
        :key="index"
        align-class="align-center"
        row-class="fill-height"
      >
        <template #actionIcon>
          <v-row no-gutters class="justify-center fill-height align-center" :class="getLadderClass(index)">
            <v-col cols="auto">
              <div class="outSideIcon">
                <v-icon color="#fff" small> {{ item.icon }} </v-icon>
              </div>
            </v-col>
          </v-row>
        </template>
        <template #default>
          <v-row no-gutters class="historyContainer my-2">
            <v-col cols="11">
              <div class="font-italic font-weight-light pa-2 text-left" style="font-size: 8px">
                {{ item.date }}
              </div>
              <div class="historyText" style="font-size: 11px">
                {{ item.text }}
              </div>

              <template v-if="item.changes">
                <div v-for="(change, index) in item.changes" :key="index" class="changesTextRow">
                  <template v-if="change.attribute_name === 'geom'">
                    <span class="newValue">{{ `&#8594;  ${$i18n.t('sidebar.geometryChanged')}` }}</span>
                  </template>
                  <template v-if="change.attribute_verbose_name && change.attribute_name !== 'geom'">
                    <span class="attributeValue">{{ `${change.attribute_verbose_name}:  ` }}</span>
                    <span class="oldValue">{{ change.old_value }}</span>
                    <span>{{ '  &#8594;  ' }}</span>
                    <span class="newValue">{{ change.new_value }}</span>
                  </template>
                </div>
              </template>
            </v-col>
            <v-col cols="1" class="pt-2">
              <dots-menu :items="item.dotsItems" @restore="openRestoreDialog(item.id)" />
            </v-col>
          </v-row>
        </template>
      </sidebar-row>
    </v-col>
    <v-col v-else>
      <main-loader />
    </v-col>
    <main-dialog
      v-if="restoreDialogLoaded"
      :is-visible.sync="restoreDialogVisible"
      :loading="restoreDialogLoading"
      :title="$i18n.t('dialog.title.restoreHistory')"
      :body="$i18n.t('dialog.body.restoreHistoryConfirmation')"
      agree-text="dialog.agreeRestore"
      @agree="restore"
    />
  </v-row>
</template>

<script>
import { call, get } from 'vuex-pathify';
import DotsMenu from '@/components/DotsMenu';
import MainDialog from '@/components/MainDialog';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'ChangeHistory',
  components: { DotsMenu, SidebarRow, MainDialog, MainLoader },
  props: {
    layerId: {
      type: [String, Number],
    },
    featureId: {
      type: [String, Number],
      required: true,
    },
    subFeatureId: {
      type: [String, Number],
    },
    dataSourceName: {
      type: String,
      required: true,
    },
    subDataSourceName: {
      type: String,
    },
  },
  data: () => ({
    currentRestoreItemId: null,
    isFetched: false,
    restoreDialogLoaded: false,
    restoreDialogVisible: false,
    restoreDialogLoading: false,
  }),
  computed: {
    history: get('changeHistory/history'),
    activeDataSourceName() {
      return this.subDataSourceName || this.dataSourceName;
    },
    activeFeatureId() {
      return this.subFeatureId || this.subFeatureId === 0 ? this.subFeatureId : this.featureId;
    },
    computedHistory() {
      return this.history.map(item => {
        return {
          ...item,
          icon: this.getItemIcon(item.change_type),
          date: this.dateText(item.timestamp),
          text: this.eventText(item),
          ...(item.changes.length > 0 && {
            changes: item.changes.map(change => {
              return {
                ...change,
                old_value: this.getValue(change.old_value),
                new_value: this.getValue(change.new_value),
              };
            }),
          }),
          dotsItems: this.getDotsItems(item),
        };
      });
    },
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
  },
  methods: {
    getHistory: call('changeHistory/getHistory'),
    restoreHistory: call('changeHistory/restoreHistory'),
    getDotsItems(item) {
      const canBeRestored = item.change_type === 'updated';
      return [
        {
          name: 'restore',
          disabled: canBeRestored ? false : true,
          tooltip: canBeRestored ? '' : this.$i18n.t('menu.restoreTooltip'),
        },
      ];
    },
    openRestoreDialog(value) {
      this.currentRestoreItemId = value;
      this.restoreDialogLoading = false;
      this.restoreDialogLoaded = true;
      this.restoreDialogVisible = true;
    },
    async restore() {
      try {
        this.restoreDialogLoaded = true;
        this.restoreDialogVisible = true;
        this.restoreDialogLoading = true;
        await this.restoreHistory({
          id: this.currentRestoreItemId,
          params: { ...(this.layerId ? { layer_id: this.layerId } : {}) },
        });
        this.currentRestoreItemId = null;
        this.restoreDialogVisible = false;
        this.init();
        this.$root.$emit('refreshTableData');
        this.$root.$emit('refreshFormData');
        const layersIds = this.projectLayers
          .filter(layer => layer.data_source_name === this.activeDataSourceName)
          .map(layer => layer.id);
        if (layersIds?.length) {
          this.$root.$emit('updateLegendStylesCounts', { layersIds });
          this.$root.$emit(
            'refreshMvtSources',
            layersIds.map(layerId => ({ id: layerId, group: 'layers' }))
          );
        }
      } finally {
        this.restoreDialogLoading = false;
      }
    },
    getValue(change) {
      return change == 0 || change === null ? 'brak danych' : change;
    },
    getLadderClass(index) {
      if (index === 0) {
        return 'ladderFirst';
      } else if (index === this.computedHistory.length - 1) {
        return 'ladderLast';
      } else {
        return 'ladder';
      }
    },
    async init() {
      this.isFetched = false;
      await this.getHistory({
        data_source_name: this.activeDataSourceName,
        feature_id: this.activeFeatureId,
      });
      this.isFetched = true;
    },
    eventText(item) {
      const { user_name, change_type } = item;
      return this.$i18n.t(`sidebar.object_${change_type}`, { user_name });
    },
    dateText(date) {
      if (date === undefined) {
        return this.$i18n.t('admin.noData');
      } else {
        return this.$dayjs(date).format('L, LTS');
      }
    },
    getItemIcon(value) {
      if (value === 'inserted') {
        return 'mdi-plus';
      } else if (value === 'updated') {
        return 'mdi-pencil';
      } else return 'mdi-delete';
    },
  },
  mounted() {
    this.$watch(
      vm => {
        const { activeDataSourceName, activeFeatureId } = vm;
        return { activeDataSourceName, activeFeatureId };
      },
      nV => {
        const { activeDataSourceName, activeFeatureId } = nV;
        if (activeDataSourceName && (activeFeatureId || activeFeatureId === 0)) this.init();
      }
    );
    this.init();
    this.$root.$off('getObjectHistory');
    this.$root.$on('getObjectHistory', this.init);
  },
};
</script>

<style scoped lang="scss">
.historyContainer {
  border: 1px solid rgba(0, 0, 0, 0.12);
}
.outSideIcon {
  background: #1a73e8;
  box-shadow: 0 1px 5px 0 #0000001f, 0 2px 2px 0 #00000024, 0 1px 1px 0 #00000033;

  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid white;
  position: relative;
  z-index: 1;
  border-radius: 50%;
  height: 34px;
  width: 34px;
}
.ladder,
.ladderFirst,
.ladderLast {
  position: relative;
}
.ladder::before {
  content: '';
  height: 100%;
  position: absolute;
  z-index: 0;
  top: 0;
  left: 50%;
  border-left: 2px solid rgba(0, 0, 0, 0.12);
}
.ladderFirst::before {
  content: '';
  height: 50%;
  position: absolute;
  z-index: 0;
  top: 50%;
  left: 50%;
  border-left: 2px solid rgba(0, 0, 0, 0.12);
}
.ladderLast::before {
  content: '';
  height: 50%;
  position: absolute;
  z-index: 0;
  top: 0;
  left: 50%;
  border-left: 2px solid rgba(0, 0, 0, 0.12);
}
.historyText {
  padding: 0 40px 4px 7px;
  text-align: left;
}
.changesTextRow {
  padding: 4px 40px 4px 7px;
  text-align: left;
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 11px;
  line-height: 13px;
}
.no-content {
  font-size: 18px;
  line-height: 16px;
  color: #aaaaaa;
  padding: 1.5rem;
}
.attributeValue {
  color: #00000096;
}
.oldValue {
  color: #bf360c;
}
.newValue {
  color: #188038;
}
</style>
